import { ContentType } from "@/api/scode/scode_params";
import { usePosterSwipePro } from "@/components/poster-swiper/pro";
import { platform } from "@/utils/platform";
import { useShare } from "@/utils/share/share";
import { versionsCompare } from "@/utils/version-compare";

import { getAppSystem } from "@/utils/app-system";
/**
 * 榜单分享Hook
 * 用于处理榜单页面的分享逻辑，包括榜单分享和商品分享
 */
export interface RankShareConfig {
  // 榜单分享配置
  rankShare: {
    contentType: ContentType; // 内容类型：榜单中心或类目榜单
    oneCategoryId?: string;
    contentId: string; // 内容ID：榜单ID或类目ID
    // title?: string; // 分享标题
  };
}

/**
 * 榜单列表分享Hook
 * @param config 榜单分享配置
 * @param config.rankShare 榜单分享配置
 * @param config.rankShare.contentType 榜单分享类型，传ContentType.rankCenter
 * @param config.rankShare.oneCategoryId 一级类目ID，
 * @param config.rankShare.contentId 榜单分享内容ID，传二级类目ID
//  * @param config.rankShare.title 榜单分享标题,二级级榜单名称，
 * @returns 分享相关的控制器和方法
 */
export const useRankListShare = (config: RankShareConfig) => {
  const { rankShare } = config;
  const {  sharePageContent } = useShare();

  // 榜单、商品各生成一套配置，用于不同的分享场景
  // 榜单分享海报配置（包含多个榜单和单个榜单场景），要配合@/components/poster-swiper/pro组件使用
  const swiperControl = usePosterSwipePro({
    defaultBizInfo: {
      contentType: rankShare.contentType,
      contentExtra: {
        posterCode: "TotalHotListPoster",
      },
    },
    defaultEnvContext: {
      sourceScene: rankShare.contentType,
      shareFrom: "MANUAL",
      scodeType: "SHARE",
      targetTerminalType: "H5",
    },
    defaultCarrierContext: {
      carrierElements: ["h5POSTER"],
      posterInfo: {
        posterCode: "TotalHotListPoster",
      },
    },
    defaultContentContext: {
      contentType: rankShare.contentType,
      contentExtra: {
        posterCode: "TotalHotListPoster",
      },
    },
    defaultUIConfig: {
      btnConfig: ["copy", "edit"],
    },
    onNonH5Open: async options => {
      // 确保contentId有值
      if (!rankShare.contentId && !rankShare.oneCategoryId) {
        console.warn("非H5榜单分享失败：contentId为空");
        return;
      }

      // 加传扩展字段，新老版本都加
      const contentExtra = encodeURIComponent(
        JSON.stringify({
          oneCategoryId: rankShare.oneCategoryId,
        }),
      );

      // eslint-disable-next-line no-underscore-dangle
      let _contentId = "";
      if (platform.isApp) {
        const appVersion = (await getAppSystem())?.appVersion;
        // 版本号高于7.6.0，contentId正常传二级tabCode
        if (appVersion && versionsCompare(appVersion, "7.76.0") >= 0) {
          _contentId = rankShare.contentId || "";
        } else {
          _contentId = `${rankShare.oneCategoryId || ""},${rankShare.contentId || ""}`; //老版本一级类目ID、二级类目ID拼接，版本推全后再删除该逻辑
        }
      } else if (platform.isMini) {
        _contentId = `${rankShare.oneCategoryId || ""},${rankShare.contentId || ""}`; //小程序版本暂时未全量，先按拼接方式传，等20250915版本全量后再改成直接传二级tabCode
      }

      sharePageContent({
        miniOptions: {
          contentId: _contentId,
          contentType: rankShare.contentType,
          pageName: "榜单中心页",
          shareType: "HOT_LIST_TOTAL",
          posterCode: "TotalHotListPoster",
          contentExtra,
        },
        appOptions: {
          contentType: "HOT_LIST_TOTAL",
          contentId: _contentId,
          pageName: "榜单中心页",
          src: "HOT_LIST_TOTAL",
          sid: _contentId,
          shareType: "HOT_LIST_TOTAL",
          posterCode: "TotalHotListPoster",
          imgType: "TotalHotListPoster",
          contentExtra,
        },
      });
    },
  });



  return {
    swiperControl,
  };
};
